import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UseInactivityTimeoutOptions {
  /**
   * Duree d'inactivite avant deconnexion (en minutes)
   * @default 30
   */
  timeoutMinutes?: number;

  /**
   * Active ou desactive le timeout
   * @default true
   */
  enabled?: boolean;

  /**
   * Callback appele juste avant la deconnexion
   */
  onBeforeLogout?: () => void;
}

/**
 * Hook de timeout d'inactivite
 * Deconnecte automatiquement l'utilisateur apres X minutes d'inactivite
 *
 * Recommande pour :
 * - Panel admin (donnees sensibles)
 * - Pages avec informations financieres
 *
 * @example
 * // Dans AdminLayout.tsx
 * useInactivityTimeout({ timeoutMinutes: 30, enabled: true });
 */
export function useInactivityTimeout({
  timeoutMinutes = 30,
  enabled = true,
  onBeforeLogout
}: UseInactivityTimeoutOptions = {}) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleLogout = useCallback(async () => {
    // Callback optionnel avant logout
    onBeforeLogout?.();

    // Notification
    toast({
      title: 'Session expiree',
      description: 'Vous avez ete deconnecte pour inactivite.',
      variant: 'default'
    });

    // Deconnexion
    await supabase.auth.signOut();

    // Vider le cache
    queryClient.clear();

    // Redirection
    navigate('/auth');
  }, [toast, navigate, queryClient, onBeforeLogout]);

  const resetTimer = useCallback(() => {
    // Clear le timeout existant
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Nouveau timeout
    const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
    timeoutRef.current = setTimeout(handleLogout, TIMEOUT_MS);
  }, [timeoutMinutes, handleLogout]);

  useEffect(() => {
    if (!enabled) return;

    // Evenements d'activite utilisateur
    const activityEvents = [
      'mousemove',
      'keypress',
      'keydown',
      'click',
      'scroll',
      'touchstart',
      'touchmove'
    ];

    // Throttle pour eviter trop de resets
    let lastActivity = Date.now();
    const THROTTLE_MS = 1000; // 1 seconde

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > THROTTLE_MS) {
        lastActivity = now;
        resetTimer();
      }
    };

    // Ajouter les listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Demarrer le timer initial
    resetTimer();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, resetTimer]);

  // Retourner des fonctions utiles
  return {
    /**
     * Reset manuellement le timer (ex: apres une action importante)
     */
    resetTimer,

    /**
     * Temps restant approximatif (pour affichage eventuel)
     */
    timeoutMinutes
  };
}
